import React, { useState } from 'react';

interface Exercise {
  name: string;
  sets?: number;
  reps?: string | number;
  rest_seconds?: number;
  duration_seconds?: number;
  instructions?: string;
  form_cues?: string[];
  target_muscles?: string[];
  equipment?: string[];
  modifications?: string;
  safety_notes?: string[]; // Rendered in timer rest-period Form Tips only
  image_url?: string;  // Legacy field
  media_urls?: string[];  // 🆕 JAN 31, 2026: Backend stores images here (array)
  images?: string[];  // 🆕 JAN 31, 2026: Alternative field name
  exercise_type?: 'reps' | 'hold' | 'flow';
}

interface Phase {
  phase_order: number;
  phase_type: string;
  phase_name: string;
  duration_seconds: number;
  instructions?: string;
  exercises?: Exercise[];
  exercises_json?: Exercise[];
}

interface ExpandablePhaseSectionProps {
  phase: Phase;
  phaseIndex: number;
  startingExerciseNumber: number;
  isDefaultExpanded?: boolean;
}

/**
 * Expandable Phase Section Component
 * 🆕 JAN 29, 2026: Complete phase-based UI restructuring
 * 
 * Features:
 * - Collapsible phase headers with descriptions as subtitles
 * - Nested exercise collapsible sections within each phase
 * - Emoji logic for phase types (🔥💪🎯🧘)
 * - Conditional display based on exercise_type (reps/hold/flow)
 * - Image rendering with fallback
 * - Removed redundant text from headers
 */
export const ExpandablePhaseSection: React.FC<ExpandablePhaseSectionProps> = ({
  phase,
  phaseIndex,
  startingExerciseNumber,
  isDefaultExpanded = false
}) => {
  const [isPhaseExpanded, setIsPhaseExpanded] = useState(isDefaultExpanded);
  const [expandedExerciseIndex, setExpandedExerciseIndex] = useState<number | null>(null);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);  // 🆕 JAN 29, 2026: Modal for full-size image

  // Get exercises from either exercises or exercises_json
  const exercises = phase.exercises || phase.exercises_json || [];

  // Get phase emoji based on type
  const getPhaseEmoji = (phaseType: string): string => {
    switch (phaseType.toLowerCase()) {
      case 'activation':
      case 'warmup':
        return '🔥';
      case 'main_compound':
      case 'main':
        return '💪';
      case 'accessory':
      case 'isolation':
        return '🎯';
      case 'cooldown':
      case 'mobility':
        return '🧘';
      default:
        return '💪';
    }
  };

  // Calculate phase duration in minutes
  const phaseDurationMin = Math.round((phase.duration_seconds || 0) / 60);

  // Toggle phase expansion
  const togglePhase = () => {
    setIsPhaseExpanded(!isPhaseExpanded);
    if (!isPhaseExpanded) {
      setExpandedExerciseIndex(null); // Collapse all exercises when collapsing phase
    }
  };

  // Toggle individual exercise
  const toggleExercise = (idx: number) => {
    setExpandedExerciseIndex(expandedExerciseIndex === idx ? null : idx);
  };

  return (
    <>
    <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
      {/* Phase Header - Collapsible */}
      <button
        className="w-full text-left p-4 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between"
        onClick={togglePhase}
      >
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg">{getPhaseEmoji(phase.phase_type)}</span>
            <h3 className="text-base font-semibold text-gray-900">
              {phase.phase_name}
            </h3>
            <span className="text-sm text-gray-500">• {phaseDurationMin} min</span>
          </div>
          {/* Phase description as subtitle */}
          {phase.instructions && (
            <p className="text-sm text-gray-600 ml-7 mt-1">
              {phase.instructions}
            </p>
          )}
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ml-2 ${
            isPhaseExpanded ? 'transform rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Phase Exercises - Nested Collapsible Sections */}
      {isPhaseExpanded && (
        <div className="bg-white p-4 space-y-3">
          {exercises.map((exercise, idx) => {
            const isExerciseExpanded = expandedExerciseIndex === idx;
            const exerciseNumber = startingExerciseNumber + idx;
            const exerciseType = exercise.exercise_type || 'reps'; // Default to reps

            return (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                {/* Exercise Header - Collapsible */}
                <button
                  className="w-full text-left p-4 hover:bg-gray-50 transition-colors"
                  onClick={() => toggleExercise(idx)}
                >
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: Title, Muscles, Metrics */}
                    <div className="flex-1 min-w-0">
                      {/* Exercise Title */}
                      <h4 className="text-sm font-semibold text-gray-900 mb-1">
                        {exerciseNumber}. {exercise.name}
                      </h4>
                      
                      {/* Target Muscles */}
                      {exercise.target_muscles && exercise.target_muscles.length > 0 && (
                        <p className="text-xs text-gray-500 mb-2">
                          {exercise.target_muscles.join(' • ')}
                        </p>
                      )}
                      
                      {/* Conditional Metrics based on exercise_type */}
                      {exerciseType === 'flow' && (
                        <div className="flex gap-4 text-sm text-gray-600">
                          <span>Cycles: {exercise.reps || 6}</span>
                          {exercise.duration_seconds && (
                            <span>Duration: ~{Math.round(exercise.duration_seconds / 60)} min</span>
                          )}
                        </div>
                      )}
                      
                      {exerciseType === 'hold' && (
                        <div className="flex gap-4 text-sm text-gray-600">
                          <span>Sets: {exercise.sets || 2}</span>
                          <span>Hold: {exercise.duration_seconds || exercise.reps}s</span>
                          {exercise.rest_seconds !== undefined && exercise.rest_seconds > 0 && (
                            <span>Rest: {exercise.rest_seconds}s</span>
                          )}
                        </div>
                      )}
                      
                      {exerciseType === 'reps' && (
                        <div className="flex gap-4 text-sm text-gray-600">
                          <span>Sets: {exercise.sets || 3}</span>
                          <span>Reps: {exercise.reps || 10}</span>
                          {exercise.rest_seconds !== undefined && exercise.rest_seconds > 0 && (
                            <span>Rest: {exercise.rest_seconds}s</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Right: Image (if available) - 🆕 JAN 31, 2026: Fixed to use media_urls array */}
                    {(() => {
                      // 🔧 JAN 31, 2026: Backend stores images as media_urls array, not image_url string
                      const imageUrl = exercise.image_url || 
                                      (exercise.media_urls && exercise.media_urls.length > 0 ? exercise.media_urls[0] : null) ||
                                      (exercise.images && exercise.images.length > 0 ? exercise.images[0] : null);
                      
                      if (!imageUrl) return null;
                      
                      return (
                        <div 
                          className="flex-shrink-0 cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();  // Prevent exercise toggle
                            // 🎯 USER REQUEST: If collapsed, expand THEN show image. If expanded, just show image.
                            if (!isExerciseExpanded) {
                              setExpandedExerciseIndex(idx);  // Expand exercise
                            }
                            setFullscreenImage(imageUrl);  // Always show fullscreen
                          }}
                        >
                          <img
                            src={imageUrl}
                            alt={exercise.name}
                            className="w-20 h-20 object-cover rounded-lg border border-gray-300"
                            onError={(e) => {
                              // Fallback: Hide image if fails to load
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </div>
                      );
                    })()}
                  </div>

                  {/* Expand/Collapse Indicator */}
                  <div className="flex justify-center mt-2">
                    <svg
                      className={`w-5 h-5 text-gray-400 transition-transform ${
                        isExerciseExpanded ? 'transform rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>

                {/* Exercise Expanded Details */}
                {isExerciseExpanded && (
                  <div className="px-4 pb-4 border-t border-gray-200 bg-gray-50">
                    {/* How to Perform */}
                    {exercise.instructions && (
                      <div className="mb-4 mt-4">
                        <h5 className="text-sm font-semibold text-gray-900 mb-2">
                          How to Perform
                        </h5>
                        <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                          {exercise.instructions}
                        </div>
                      </div>
                    )}

                    {/* Form Cues */}
                    {exercise.form_cues && exercise.form_cues.length > 0 && (
                      <div className="mb-4">
                        <h5 className="text-sm font-semibold text-gray-900 mb-2">
                          Form Cues
                        </h5>
                        <ul className="space-y-1">
                          {exercise.form_cues.map((cue, cueIdx) => (
                            <li key={cueIdx} className="text-sm text-gray-700 flex items-start gap-2">
                              <span className="text-[#1F6C4F] flex-shrink-0">•</span>
                              <span>{cue}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Task 12: Yellow safety disclaimer removed */}

                    {/* Modifications */}
                    {exercise.modifications && (
                      <div className="mb-4">
                        <h5 className="text-sm font-semibold text-gray-900 mb-2">
                          Modifications
                        </h5>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {exercise.modifications}
                        </p>
                      </div>
                    )}

                    {/* Equipment */}
                    {exercise.equipment && exercise.equipment.length > 0 && (
                      <div>
                        <h5 className="text-sm font-semibold text-gray-900 mb-2">
                          Equipment
                        </h5>
                        <div className="flex flex-wrap gap-2">
                          {exercise.equipment.map((item, itemIdx) => (
                            <span
                              key={itemIdx}
                              className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded"
                            >
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
    
    {/* 🆕 JAN 29, 2026: Fullscreen Image Modal */}
    {fullscreenImage && (
      <div 
        className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
        onClick={() => setFullscreenImage(null)}
      >
        <div className="relative max-w-4xl max-h-full">
          {/* Close Button */}
          <button
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors z-10"
            onClick={() => setFullscreenImage(null)}
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {/* Full-Size Image */}
          <img 
            src={fullscreenImage} 
            alt="Exercise demonstration"
            className="w-full h-auto rounded-lg max-h-screen object-contain"
            onClick={(e) => e.stopPropagation()}  // Prevent closing when clicking image
          />
        </div>
      </div>
    )}
  </>
  );
};
